const instances = new WeakSet();

const sanitizeQuote = (value, maxLength) =>
  String(value || '')
    .replace(/[\u0000-\u001f\u007f<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const sanitizeQuotePreview = (value, maxLength) =>
  String(value || '')
    .replace(/[\u0000-\u001f\u007f<>]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, maxLength);

const wrapQuote = (quote) => {
  return [quote || 'Your quote'];
};

const QUOTE_FONTS = {
  'Classic Serif': { family: 'Georgia, "Times New Roman", serif', weight: 700 },
  'Clean Sans': { family: 'Arial, Helvetica, sans-serif', weight: 700 },
  'Luxury Caps': { family: 'Georgia, "Times New Roman", serif', weight: 700, uppercase: true, letterSpacing: 8, sizeScale: 0.86, minScale: 0.82 },
  'Roman Engrave': { family: '"Palatino Linotype", "Book Antiqua", Palatino, serif', weight: 700, uppercase: true, letterSpacing: 5, sizeScale: 0.9, minScale: 0.82 },
  'Sharp Block': { family: 'Impact, "Arial Narrow", sans-serif', weight: 700, uppercase: true, letterSpacing: 2, sizeScale: 0.92, minScale: 0.86 },
  'Wide Sans': { family: 'Arial, Helvetica, sans-serif', weight: 700, uppercase: true, letterSpacing: 7, sizeScale: 0.86, minScale: 0.82 },
  'Elegant Script': { family: '"Segoe Script", "Lucida Handwriting", cursive', weight: 600, sizeScale: 0.86, minScale: 0.94 },
  'Bold Display': { family: '"Arial Black", Impact, sans-serif', weight: 800 },
  Typewriter: { family: '"Courier New", Courier, monospace', weight: 700 },
};

const getQuoteFont = (fontName) => QUOTE_FONTS[fontName] || QUOTE_FONTS['Classic Serif'];

const PREVIEW_PRESETS = {
  'joker-rect-base': {
    scanHeights: [0.035, 0.045, 0.055, 0.07, 0.085],
    scanXOffsets: [0],
    widthScale: 0.9,
    maxWidthScale: 1.18,
    maxWidthFromDepth: 1.65,
    heightScale: 0.22,
    depthScale: 0.1,
    minDepth: 0.035,
    fallbackHeight: 0.045,
    zOffset: 0.0008,
    focusCameraYOffset: 0.1,
  },
  'naruto-round-base': {
    scanHeights: [0.025, 0.035, 0.045, 0.055, 0.07],
    scanXOffsets: [0, -0.08, 0.08],
    widthScale: 0.58,
    maxWidthScale: 0.82,
    maxWidthFromDepth: 1.24,
    heightScale: 0.23,
    depthScale: 0.12,
    minDepth: 0.035,
    fallbackHeight: 0.038,
    zOffset: 0.0012,
  },
  'sasuke-round-base': {
    scanHeights: [0.025, 0.035, 0.045, 0.055, 0.07],
    scanXOffsets: [0, -0.08, 0.08],
    widthScale: 0.58,
    maxWidthScale: 0.82,
    maxWidthFromDepth: 1.24,
    heightScale: 0.23,
    depthScale: 0.12,
    minDepth: 0.035,
    fallbackHeight: 0.038,
    zOffset: 0.0012,
  },
  'anime-nameplate-base': {
    preferredMeshNames: ['quoteplate', 'quote_plate', 'quote-plate', 'nameplate', 'name_plate', 'name-plate'],
    scanHeights: [0.088, 0.098, 0.078, 0.108, 0.068],
    scanXOffsets: [0, -0.05, 0.05],
    widthScale: 0.62,
    maxWidthScale: 0.72,
    maxWidthFromDepth: 1.16,
    heightScale: 0.18,
    depthScale: 0.08,
    minDepth: 0.03,
    fallbackHeight: 0.16,
    zOffset: 0.0015,
    textSafeWidthScale: 0.76,
    textFontScale: 0.88,
    textLetterSpacing: 1,
    focusDistanceScale: 0.36,
    focusMinDistancePadding: 1.02,
    focusCameraYOffset: 0.12,
    focusTargetYOffset: 0.01,
  },
  'named-quoteplate-base': {
    preferredMeshNames: ['quoteplate', 'quote_plate', 'quote-plate', 'nameplate', 'name_plate', 'name-plate', 'mesh_0.001'],
    scanHeights: [0.088, 0.098, 0.078, 0.108, 0.068],
    scanXOffsets: [0, -0.05, 0.05],
    widthScale: 0.92,
    maxWidthScale: 0.96,
    maxWidthFromDepth: 1.16,
    heightScale: 0.18,
    depthScale: 0.08,
    minDepth: 0.03,
    fallbackHeight: 0.16,
    zOffset: 0.0012,
    textSafeWidthScale: 0.84,
    textFontScale: 0.58,
    textLetterSpacing: 0,
    minTextScale: 0.72,
    shadowStrokeScale: 0.052,
    highlightStrokeScale: 0.018,
    textFill: 'rgba(238, 238, 224, 0.94)',
    textShadow: 'rgba(8, 7, 5, 0.62)',
    textHighlight: 'rgba(255, 255, 255, 0.24)',
    shineFill: 'rgba(255, 255, 255, 0.16)',
    focusDistanceScale: 0.34,
    focusMinDistancePadding: 1,
    focusCameraYOffset: 0.11,
    focusTargetYOffset: 0.006,
    fontOverrides: {
      'Classic Serif': { weight: 600, sizeScale: 0.9, minScale: 0.72 },
      'Clean Sans': { weight: 700, sizeScale: 0.92, minScale: 0.72 },
      'Luxury Caps': { letterSpacing: 2, sizeScale: 0.78, minScale: 0.68 },
      'Roman Engrave': { letterSpacing: 1.5, sizeScale: 0.82, minScale: 0.68 },
      'Sharp Block': { sizeScale: 0.78, minScale: 0.68 },
      'Wide Sans': { letterSpacing: 2, sizeScale: 0.76, minScale: 0.68 },
      'Elegant Script': { sizeScale: 0.76, minScale: 0.68 },
      'Bold Display': { sizeScale: 0.74, minScale: 0.68 },
      Typewriter: { sizeScale: 0.8, minScale: 0.68 },
    },
  },
};

const getPreviewPreset = (name) => PREVIEW_PRESETS[name] || PREVIEW_PRESETS['joker-rect-base'];

const QUOTE_TEXTURE_FONT_SIZE = 76;
const QUOTE_MIN_TEXT_SCALE = 1.5;
const QUOTE_BASE_LETTER_SPACING = 3;

const getQuoteWidthBoost = (quote) => {
  const length = String(quote || '').trim().length;
  if (length <= 28) return 1;
  if (length <= 42) return 1.14;
  if (length <= 52) return 1.24;
  return 1.34;
};

const drawFittedText = (context, text, x, y, safeWidth, minScale, draw) => {
  const letterSpacing = Number.parseFloat(context.letterSpacing || '0') || 0;
  const trackingWidth = text.length > 1 ? letterSpacing * (text.length - 1) : 0;
  const measuredWidth = Math.max(1, context.measureText(text).width + trackingWidth);
  const effectiveMinScale = Number.isFinite(minScale) ? minScale : QUOTE_MIN_TEXT_SCALE;
  const scaleX = Math.max(effectiveMinScale, Math.min(1, safeWidth / measuredWidth));

  context.save();
  context.translate(x, y);
  context.scale(scaleX, 1);
  draw(0, 0);
  context.restore();
};

const drawQuoteTexture = (canvas, quote, fontName, options = {}) => {
  const context = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  const lines = wrapQuote(quote);
  const font = {
    ...getQuoteFont(fontName),
    ...(options.fontOverrides?.[fontName] || {}),
  };

  context.clearRect(0, 0, width, height);
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const fontSize = QUOTE_TEXTURE_FONT_SIZE * (font.sizeScale || 1) * (options.textFontScale || 1);
  const lineHeight = fontSize * 1.08;
  const startY = height / 2 - ((lines.length - 1) * lineHeight) / 2;
  const safeTextWidth = width * (options.textSafeWidthScale || 0.92);

  lines.forEach((line, index) => {
    const y = startY + index * lineHeight;
    const displayLine = font.uppercase ? line.toUpperCase() : line;
    context.font = `${font.weight} ${fontSize}px ${font.family}`;
    context.letterSpacing = `${(font.letterSpacing || 0) + (options.textLetterSpacing ?? QUOTE_BASE_LETTER_SPACING)}px`;

    const minTextScale = options.minTextScale ?? font.minScale ?? QUOTE_MIN_TEXT_SCALE;

    drawFittedText(context, displayLine, width / 2, y, safeTextWidth, minTextScale, (drawX, drawY) => {
      context.lineWidth = Math.max(2, fontSize * (options.shadowStrokeScale ?? 0.13));
      context.strokeStyle = quote ? options.textShadow || 'rgba(2, 1, 0, 0.82)' : 'rgba(2, 1, 0, 0.34)';
      context.strokeText(displayLine, drawX, drawY + fontSize * 0.055);

      context.lineWidth = Math.max(1, fontSize * (options.highlightStrokeScale ?? 0.045));
      context.strokeStyle = quote ? options.textHighlight || 'rgba(255, 255, 255, 0.68)' : 'rgba(255, 255, 255, 0.22)';
      context.strokeText(displayLine, drawX, drawY - fontSize * 0.028);

      context.fillStyle = quote ? options.textFill || 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.38)';
      context.fillText(displayLine, drawX, drawY);

      context.fillStyle = quote ? options.shineFill || 'rgba(255, 255, 255, 0.32)' : 'rgba(255, 255, 255, 0.1)';
      context.fillText(displayLine, drawX, drawY - fontSize * 0.045);
    });
  });
};

const fitCameraToObject = (THREE, camera, object, target, viewportAspect = 1) => {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const fov = (Math.PI * camera.fov) / 180;
  const fitHeightDistance = size.y / (2 * Math.tan(fov / 2));
  const fitWidthDistance = size.x / viewportAspect / (2 * Math.tan(fov / 2));
  const distance = Math.max(fitHeightDistance, fitWidthDistance, size.z || 1) * 1.55;

  object.position.sub(center);
  camera.position.set(0, size.y * 0.04, distance);
  camera.userData.baseDistance = distance;
  camera.userData.minDistance = distance * 0.38;
  camera.userData.maxDistance = distance * 1.35;
  camera.userData.modelSizeY = size.y;
  camera.userData.modelSizeZ = size.z || 1;
  camera.near = Math.max(distance / 2000, 0.0005);
  camera.far = distance * 100;
  camera.updateProjectionMatrix();
  target.set(0, 0, 0);
  camera.userData.defaultTarget = target.clone();
  camera.userData.quoteTarget = new THREE.Vector3(0, -size.y * 0.32, 0);
};

const collectMeshes = (root) => {
  const meshes = [];
  root.traverse((child) => {
    if (child.isMesh && child.geometry) meshes.push(child);
  });
  return meshes;
};

const findNamedPlateHit = (THREE, meshes, preferredNames) => {
  if (!preferredNames?.length) return null;

  const normalizedNames = preferredNames.map((name) => String(name).toLowerCase());
  const candidates = meshes.filter((mesh) => {
    const meshName = String(mesh.name || '').toLowerCase();
    const parentName = String(mesh.parent?.name || '').toLowerCase();
    return normalizedNames.some((name) => meshName.includes(name) || parentName.includes(name));
  });

  if (!candidates.length) return null;

  let best = null;
  let bestArea = 0;

  candidates.forEach((mesh) => {
    const box = new THREE.Box3().setFromObject(mesh);
    const size = box.getSize(new THREE.Vector3());
    const area = Math.max(size.x * size.y, size.x * size.z, size.y * size.z);
    if (area > bestArea) {
      bestArea = area;
      best = { mesh, box, size };
    }
  });

  if (!best) return null;

  const center = best.box.getCenter(new THREE.Vector3());
  center.z = best.box.max.z + Math.max(best.size.z * 0.02, 0.001);

  return {
    hit: {
      object: best.mesh,
      point: center,
    },
    namedPlate: true,
    targetSize: best.size,
  };
};

const findStandHit = (THREE, modelGroup, meshes, preset) => {
  const box = new THREE.Box3().setFromObject(modelGroup);
  const size = box.getSize(new THREE.Vector3());
  const min = box.min;
  const max = box.max;
  const namedPlateHit = findNamedPlateHit(THREE, meshes, preset.preferredMeshNames);

  if (namedPlateHit) return { ...namedPlateHit, box, size };

  const raycaster = new THREE.Raycaster();
  const direction = new THREE.Vector3(0, 0, -1);
  const scanHeights = preset.scanHeights || PREVIEW_PRESETS['joker-rect-base'].scanHeights;
  const scanXOffsets = preset.scanXOffsets || [0];

  for (const heightRatio of scanHeights) {
    for (const xOffset of scanXOffsets) {
      const origin = new THREE.Vector3(size.x * xOffset, min.y + size.y * heightRatio, max.z + size.z * 1.4);
      raycaster.set(origin, direction);
      const hits = raycaster.intersectObjects(meshes, true);
      const hit = hits.find((item) => item.object.isMesh);
      if (hit) return { hit, box, size };
    }
  }

  return { hit: null, box, size };
};

const updateQuoteTexture = (state, quote, fontName) => {
  const preset = state.previewPreset || PREVIEW_PRESETS['joker-rect-base'];
  drawQuoteTexture(state.canvas, quote, fontName, preset);
  state.texture.needsUpdate = true;
};

const rebuildDecalGeometry = (THREE, DecalGeometry, state) => {
  const preset = state.previewPreset || PREVIEW_PRESETS['joker-rect-base'];

  if (!state.meshes.length) return;

  const savedRotation = state.stageGroup.rotation.clone();
  state.stageGroup.rotation.set(0, 0, 0);
  state.stageGroup.updateMatrixWorld(true);

  if (state.decal) {
    state.decal.geometry.dispose();
    state.decal.material.dispose();
    state.decal.parent?.remove(state.decal);
    state.decal = null;
  }

  const { hit, box, size, targetSize, namedPlate } = findStandHit(THREE, state.modelGroup, state.meshes, preset);
  const quoteAreaSize = targetSize || size;
  const width = namedPlate
    ? quoteAreaSize.x * preset.widthScale
    : Math.min(
        quoteAreaSize.x * (preset.maxWidthScale || preset.widthScale),
        quoteAreaSize.x * preset.widthScale,
        quoteAreaSize.z * preset.maxWidthFromDepth
      );
  const height = namedPlate
    ? Math.max(quoteAreaSize.y * (preset.heightScale || 0.9), width * 0.1)
    : width * preset.heightScale;
  const depth = namedPlate
    ? Math.max(Math.min(quoteAreaSize.z || preset.minDepth, width * 0.04), preset.minDepth)
    : Math.max(quoteAreaSize.z * preset.depthScale, preset.minDepth);
  const position = hit
    ? hit.point.clone().add(new THREE.Vector3(0, 0, preset.zOffset))
    : new THREE.Vector3(0, box.min.y + size.y * preset.fallbackHeight, box.max.z + size.z * 0.006);
  const orientation = new THREE.Euler(0, 0, 0);
  const decalSize = new THREE.Vector3(width, height, depth);
  const focusTarget = position.clone();
  focusTarget.y += size.y * (preset.focusTargetYOffset || 0);
  focusTarget.z += size.z * (preset.focusTargetZOffset || 0);
  state.quoteFocusTarget = focusTarget;

  const material = new THREE.MeshBasicMaterial({
    map: state.texture,
    transparent: true,
    side: THREE.DoubleSide,
    alphaTest: 0.03,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });

  state.decal = new THREE.Mesh(new DecalGeometry(hit?.object || state.meshes[0], position, orientation, decalSize), material);
  state.decal.renderOrder = 12;
  state.decalParent.add(state.decal);

  state.stageGroup.rotation.copy(savedRotation);
  state.stageGroup.updateMatrixWorld(true);
};

const createOrUpdateDecal = (THREE, DecalGeometry, state, quote, fontName, { rebuild = false } = {}) => {
  updateQuoteTexture(state, quote, fontName);
  if (!state.decal || rebuild) rebuildDecalGeometry(THREE, DecalGeometry, state);
};

const initViewer = async (root) => {
  if (instances.has(root)) return;
  instances.add(root);

  const mount = root.querySelector('[data-mw-joker-canvas]');
  const status = root.querySelector('[data-mw-joker-status]');
  const loading = root.querySelector('[data-mw-joker-loading]');
  const loadingText = loading?.querySelector('.mw-joker-viewer__loading-text');
  let input = document.querySelector(root.dataset.inputSelector || '[data-mw-quote-input]');
  let fontSelect = document.querySelector(root.dataset.fontSelector || '[data-mw-quote-font]');
  const modelUrl = root.dataset.modelUrl;
  const threeUrl = root.dataset.threeUrl;
  const gltfLoaderUrl = root.dataset.gltfLoaderUrl;
  const dracoLoaderUrl =
    root.dataset.dracoLoaderUrl || 'https://esm.sh/three@0.160.1/examples/jsm/loaders/DRACOLoader.js';
  const dracoDecoderPath =
    root.dataset.dracoDecoderPath || 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';
  const decalGeometryUrl = root.dataset.decalGeometryUrl;
  const maxLength = Number.parseInt(root.dataset.maxLength || '60', 10);
  const previewPreset = getPreviewPreset(root.dataset.quotePreset);

  if (!mount || !modelUrl || !threeUrl || !gltfLoaderUrl || !decalGeometryUrl) return;

  const setLoadingText = (message) => {
    if (loadingText) loadingText.textContent = message;
  };

  const hideLoading = () => {
    if (loading) loading.classList.add('is-hidden');
  };

  if (!input || !fontSelect) {
    for (let attempt = 0; attempt < 20 && (!input || !fontSelect); attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 100));
      input = document.querySelector(root.dataset.inputSelector || '[data-mw-quote-input]');
      fontSelect = document.querySelector(root.dataset.fontSelector || '[data-mw-quote-font]');
    }
  }

  if (!input || !fontSelect) {
    if (status) status.textContent = 'Quote field could not be found.';
    setLoadingText('Quote field could not be found. Please refresh the page.');
    return;
  }

  input.maxLength = maxLength;

  let THREE;
  let GLTFLoader;
  let DRACOLoader;
  let DecalGeometry;

  try {
    setLoadingText('Preparing 3D preview...');
    const [threeModule, loaderModule, dracoModule, decalModule] = await Promise.all([
      import(threeUrl),
      import(gltfLoaderUrl),
      import(dracoLoaderUrl),
      import(decalGeometryUrl),
    ]);
    THREE = threeModule;
    GLTFLoader = loaderModule.GLTFLoader;
    DRACOLoader = dracoModule.DRACOLoader;
    DecalGeometry = decalModule.DecalGeometry;
  } catch (error) {
    if (status) status.textContent = '3D quote preview could not load.';
    setLoadingText('3D quote preview could not load. Please refresh or try again later.');
    return;
  }

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  const pixelRatioCap = window.matchMedia('(max-width: 749px)').matches ? 1.25 : 1.75;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, pixelRatioCap));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  mount.appendChild(renderer.domElement);

  scene.add(new THREE.AmbientLight(0xffffff, 1.26));

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.48);
  keyLight.position.set(1.8, 4.2, 3.2);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xdceeff, 0.72);
  fillLight.position.set(-2.6, 1.8, 2.4);
  scene.add(fillLight);

  const rimLight = new THREE.DirectionalLight(0xc8e4ef, 0.8);
  rimLight.position.set(-1.8, 2.4, -2.6);
  scene.add(rimLight);

  const baseLight = new THREE.DirectionalLight(0xffdca3, 0.62);
  baseLight.position.set(0, 0.55, 3.6);
  scene.add(baseLight);

  const stageGroup = new THREE.Group();
  scene.add(stageGroup);

  const modelGroup = new THREE.Group();
  stageGroup.add(modelGroup);

  const canvas = document.createElement('canvas');
  canvas.width = 4096;
  canvas.height = 320;

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);

  const state = {
    canvas,
    decal: null,
    decalParent: stageGroup,
    meshes: [],
    modelGroup,
    quoteFocusTarget: null,
    stageGroup,
    texture,
    previewPreset,
  };

  const target = new THREE.Vector3();
  const activePointers = new Map();
  let isDragging = false;
  let previousX = 0;
  let previousY = 0;
  let previousPinchDistance = 0;
  let quoteFocusAnimation = 0;
  let quoteFocusPending = false;
  let shouldFocusQuoteOnTyping = true;

  const clampZoom = (distance) =>
    Math.max(camera.userData.minDistance || 1, Math.min(camera.userData.maxDistance || 10, distance));

  const getPinchDistance = () => {
    const pointers = [...activePointers.values()];
    if (pointers.length < 2) return 0;
    return Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y);
  };
  const focusQuotePlate = () => {
    const baseDistance = camera.userData.baseDistance;
    if (!baseDistance) {
      quoteFocusPending = true;
      return;
    }

    window.cancelAnimationFrame(quoteFocusAnimation);

    const minDistance = camera.userData.minDistance || baseDistance * 0.38;
    const focusMinDistancePadding = previewPreset.focusMinDistancePadding || 1.08;
    const focusDistance = clampZoom(
      Math.max(minDistance * focusMinDistancePadding, baseDistance * (previewPreset.focusDistanceScale || 0.45))
    );
    const quoteTarget = state.quoteFocusTarget || camera.userData.quoteTarget;
    const modelSizeY = camera.userData.modelSizeY || 1;
    const startZ = camera.position.z;
    const startY = camera.position.y;
    const focusY = quoteTarget
      ? quoteTarget.y + modelSizeY * (previewPreset.focusCameraYOffset ?? 0.1)
      : startY;
    const startRotX = stageGroup.rotation.x;
    const startRotY = THREE.MathUtils.euclideanModulo(stageGroup.rotation.y + Math.PI, Math.PI * 2) - Math.PI;
    const startTime = performance.now();
    const duration = 460;

    stageGroup.rotation.y = startRotY;

    const animateFocus = (time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      camera.position.z = startZ + (focusDistance - startZ) * eased;
      camera.position.y = startY + (focusY - startY) * eased;
      stageGroup.rotation.x = startRotX + (0 - startRotX) * eased;
      stageGroup.rotation.y = startRotY + (0 - startRotY) * eased;

      if (progress < 1) {
        quoteFocusAnimation = window.requestAnimationFrame(animateFocus);
        return;
      }

      quoteFocusAnimation = 0;
    };

    quoteFocusAnimation = window.requestAnimationFrame(animateFocus);
    startRendering();
  };

  const requestQuoteFocus = () => {
    const quote = sanitizeQuotePreview(input.value, maxLength).trim();
    if (!quote) {
      shouldFocusQuoteOnTyping = true;
      return;
    }

    if (!shouldFocusQuoteOnTyping) return;
    shouldFocusQuoteOnTyping = false;
    window.requestAnimationFrame(focusQuotePlate);
  };

  mount.addEventListener('pointerdown', (event) => {
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    mount.setPointerCapture(event.pointerId);

    if (activePointers.size >= 2) {
      isDragging = false;
      previousPinchDistance = getPinchDistance();
      return;
    }

    isDragging = true;
    previousX = event.clientX;
    previousY = event.clientY;
  });

  mount.addEventListener('pointermove', (event) => {
    if (!activePointers.has(event.pointerId)) return;
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointers.size >= 2) {
      const nextPinchDistance = getPinchDistance();
      if (previousPinchDistance > 0 && nextPinchDistance > 0) {
        const zoomFactor = previousPinchDistance / nextPinchDistance;
        camera.position.z = clampZoom(camera.position.z * zoomFactor);
      }
      previousPinchDistance = nextPinchDistance;
      return;
    }

    if (!isDragging) return;
    const deltaX = event.clientX - previousX;
    const deltaY = event.clientY - previousY;
    previousX = event.clientX;
    previousY = event.clientY;

    stageGroup.rotation.x = Math.max(-0.45, Math.min(0.45, stageGroup.rotation.x + deltaY * 0.006));
    stageGroup.rotation.y += deltaX * 0.008;
  });

  const endPointer = (event) => {
    activePointers.delete(event.pointerId);
    previousPinchDistance = activePointers.size >= 2 ? getPinchDistance() : 0;

    if (activePointers.size === 1) {
      const remainingPointer = [...activePointers.values()][0];
      isDragging = true;
      previousX = remainingPointer.x;
      previousY = remainingPointer.y;
      return;
    }

    isDragging = false;
  };

  mount.addEventListener('pointerup', endPointer);
  mount.addEventListener('pointercancel', endPointer);

  mount.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      const zoomFactor = event.deltaY > 0 ? 1.08 : 0.92;
      camera.position.z = clampZoom(camera.position.z * zoomFactor);
    },
    { passive: false }
  );

  const syncQuote = ({ commit = true, rebuild = false } = {}) => {
    const quote = commit ? sanitizeQuote(input.value, maxLength) : sanitizeQuotePreview(input.value, maxLength);
    if (commit) input.value = quote;
    createOrUpdateDecal(THREE, DecalGeometry, state, quote.trim(), fontSelect.value, { rebuild });
    if (status) status.textContent = quote.trim() ? 'Quote preview updated on stand.' : 'Quote cleared from preview.';
  };

  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    syncQuote({ commit: true });
    input.blur();
  });
  input.addEventListener('blur', () => syncQuote({ commit: true }));
  input.addEventListener('change', () => syncQuote({ commit: true }));
  fontSelect.addEventListener('change', () => syncQuote({ commit: false, rebuild: true }));

  let livePreviewFrame = 0;
  input.addEventListener('input', () => {
    requestQuoteFocus();
    window.cancelAnimationFrame(livePreviewFrame);
    livePreviewFrame = window.requestAnimationFrame(() => {
      syncQuote({ commit: false });
      if (status) status.textContent = input.value.trim() ? 'Live quote preview updated.' : 'Quote cleared from preview.';
    });
  });

  const resize = () => {
    const rect = mount.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };

  const loader = new GLTFLoader();
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(dracoDecoderPath);
  loader.setDRACOLoader(dracoLoader);
  setLoadingText('Loading your 3D quote preview. This may take a moment on slower internet.');
  loader.load(
    modelUrl,
    (gltf) => {
      modelGroup.add(gltf.scene);
      state.meshes = collectMeshes(modelGroup);
      const rect = mount.getBoundingClientRect();
      fitCameraToObject(THREE, camera, modelGroup, target, Math.max(1, rect.width) / Math.max(1, rect.height));
      syncQuote({ rebuild: true });
      if (quoteFocusPending) {
        quoteFocusPending = false;
        window.requestAnimationFrame(focusQuotePlate);
      }
      if (status) status.textContent = '';
      hideLoading();
      dracoLoader.dispose();
      resize();
    },
    (event) => {
      if (!event.lengthComputable) return;
      const progress = Math.min(99, Math.round((event.loaded / event.total) * 100));
      setLoadingText(`Loading 3D quote preview... ${progress}%`);
    },
    () => {
      if (status) status.textContent = '3D model could not load.';
      setLoadingText('3D model could not load. Please refresh or try again later.');
      dracoLoader.dispose();
    }
  );

  let animationFrame = 0;
  let isViewerVisible = false;
  let isPageScrolling = false;
  let scrollResumeTimer = 0;

  const updateCameraTarget = () => {
    const baseDistance = camera.userData.baseDistance || camera.position.z;
    const minDistance = camera.userData.minDistance || baseDistance;
    const defaultTarget = camera.userData.defaultTarget || target;
    const quoteTarget = state.quoteFocusTarget || camera.userData.quoteTarget || target;
    const zoomRange = Math.max(0.001, baseDistance - minDistance);
    const zoomProgress = Math.max(0, Math.min(1, (baseDistance - camera.position.z) / zoomRange));
    target.lerpVectors(defaultTarget, quoteTarget, zoomProgress);
  };

  const render = () => {
    if (!isViewerVisible || isPageScrolling || document.hidden) {
      animationFrame = 0;
      return;
    }

    updateCameraTarget();
    camera.lookAt(target);
    renderer.render(scene, camera);
    animationFrame = requestAnimationFrame(render);
  };

  const startRendering = () => {
    if (animationFrame || !isViewerVisible || isPageScrolling || document.hidden) return;
    animationFrame = requestAnimationFrame(render);
  };

  const stopRendering = () => {
    if (!animationFrame) return;
    cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  };

  const handlePageScroll = () => {
    isPageScrolling = true;
    stopRendering();
    window.clearTimeout(scrollResumeTimer);
    scrollResumeTimer = window.setTimeout(() => {
      isPageScrolling = false;
      startRendering();
    }, 120);
  };

  window.addEventListener('scroll', handlePageScroll, { passive: true });
  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      isViewerVisible = entry.isIntersecting;
      if (isViewerVisible) {
        resize();
        startRendering();
      } else {
        stopRendering();
      }
    },
    { rootMargin: '160px 0px', threshold: 0.01 }
  );

  new ResizeObserver(resize).observe(mount);
  visibilityObserver.observe(root);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopRendering();
    else startRendering();
  });
  resize();
};

const initAllJokerViewers = () => {
  const roots = document.querySelectorAll('[data-mw-joker-viewer]');

  if (!('IntersectionObserver' in window)) {
    roots.forEach(initViewer);
    return;
  }

  const loadObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        initViewer(entry.target);
      });
    },
    { rootMargin: '320px 0px', threshold: 0.01 }
  );

  roots.forEach((root) => loadObserver.observe(root));
};

initAllJokerViewers();




